import { ReactElement } from "react";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { components, paths } from "../types/voislog_api";
import { sentryLog } from "../../sentry";
import { ApiError } from "../utils/apiClients/ApiError";
import {
  FilterKeys,
  SuccessResponse,
  ResponseObjectMap,
  MediaType,
  PathsWithMethod,
} from "openapi-typescript-helpers";

type RecordingResponse = components["schemas"]["RecordingResponseModel"];

type EngineType = components["schemas"]["EngineType"];
type AnalysisPath<T extends EngineType> =
  `/v2/core/analysis/${T}/recordings/{recording_id}`;

// post を持つエンジンのみに絞る
export type AnalyzableEngineType = {
  [T in EngineType]: AnalysisPath<T> extends PathsWithMethod<paths, "post">
    ? T
    : never;
}[EngineType];

type AnalysisGetOperation<T extends EngineType> = paths[AnalysisPath<T>]["get"];
type AnalysisPostOperation<T extends AnalyzableEngineType> =
  paths[AnalysisPath<T>]["post"];
export type AnalysisResult<T extends EngineType> = SuccessResponse<
  ResponseObjectMap<AnalysisGetOperation<T>>,
  MediaType
>;
type AnalysisPostBody<T extends AnalyzableEngineType> = NonNullable<
  FilterKeys<
    FilterKeys<NonNullable<AnalysisPostOperation<T>["requestBody"]>, "content">,
    "application/json"
  >
>;

type ApiGet<T extends EngineType> = (
  url: AnalysisPath<T>,
  init: { params: { path: { recording_id: string } } },
) => Promise<{ data?: AnalysisResult<T>; error?: unknown; response: Response }>;

type ApiPost<T extends AnalyzableEngineType> = (
  url: AnalysisPath<T>,
  init: {
    params: { path: { recording_id: string } };
    body?: AnalysisPostBody<T>;
  },
) => Promise<{ data?: AnalysisResult<T>; error?: unknown; response: Response }>;

class AnalysisEngine<T extends AnalyzableEngineType> {
  private readonly engineType: T;
  private readonly path: AnalysisPath<T>;
  readonly resultCaches: Record<string, AnalysisResult<T>> = {};
  private readonly recordingCaches: Record<string, RecordingResponse> = {};

  constructor(engineType: T) {
    this.engineType = engineType;
    this.path = `/v2/core/analysis/${engineType}/recordings/{recording_id}`;
  }

  async getResult(recordingId: string): Promise<AnalysisResult<T> | null> {
    if (this.resultCaches[recordingId]) {
      return this.resultCaches[recordingId];
    }
    try {
      const { data, error, response } = await (voislogApi.GET as ApiGet<T>)(
        this.path,
        {
          params: { path: { recording_id: recordingId } },
        },
      );
      if (error) throw new ApiError(response, "get", error);
      this.resultCaches[recordingId] = data as AnalysisResult<T>;
      return data as AnalysisResult<T>;
    } catch (e) {
      sentryLog(e);
      return null;
    }
  }

  async getRecording(recordingId: string): Promise<RecordingResponse | null> {
    if (this.recordingCaches[recordingId]) {
      return this.recordingCaches[recordingId];
    }
    try {
      const { data, error, response } = await voislogApi.GET(
        "/v2/core/recordings/{recording_id}",
        {
          params: { path: { recording_id: recordingId } },
        },
      );
      if (error) throw new ApiError(response, "get", error);
      if (data) this.recordingCaches[recordingId] = data;
      return data ?? null;
    } catch (e) {
      sentryLog(e);
      return null;
    }
  }

  async analyze(
    recordingId: string,
    body?: AnalysisPostBody<T>,
  ): Promise<AnalysisResult<T>> {
    const startTime = performance.now();
    const { data, error, response } = await (voislogApi.POST as ApiPost<T>)(
      this.path,
      {
        params: { path: { recording_id: recordingId } },
        body,
      },
    );
    const elapsed = (performance.now() - startTime).toFixed(3);
    console.info(
      `[CoreAPI] /analysis/${this.engineType}/recordings/${recordingId} : ${elapsed}ms`,
    );
    if (error) throw new ApiError(response, "post", error);
    this.resultCaches[recordingId] = data as AnalysisResult<T>;
    return data as AnalysisResult<T>;
  }
}

export abstract class BaseAnalysisEngine<T extends AnalyzableEngineType> {
  protected readonly _engine: AnalysisEngine<T>;

  constructor(engineType: T) {
    this._engine = new AnalysisEngine(engineType);
  }

  abstract analyze_call(
    recordingId: string,
    detectVolume: number,
  ): Promise<unknown>;

  abstract renderResult(
    key: number,
    fileId: string,
    t: (key: string) => string,
  ): Promise<ReactElement>;

  abstract getLatestRecordedAt(recordingId: string): Promise<string>;
}
